import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import { Button } from '@/components/ui/button';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildArticleSchema, buildBreadcrumbSchema } from '@/lib/schema';

export default function CaseStudyKendall() {
  const pageData = {
    title: "Kendall Impact Window & Door Replacement — Full Home Project with Good / Better / Premium Options",
    metaDescription: "See how a Kendall homeowner compared Good/Better/Premium options and completed a $24,500 full-home impact window and door project in 8 weeks.",
    canonicalPath: "/case-study-kendall-full-house",
    
    directAnswer: (
      <>
        <p className="text-lg text-[var(--brand-slate-700)] mb-3">
          How we helped a homeowner compare options, understand pricing, and choose the right system without a sales appointment.
        </p>
        <p className="text-[var(--brand-slate-700)] mb-4 leading-relaxed">
          Most homeowners assume getting impact windows means sitting through a long sales presentation. This project shows how we simplify the process — giving clear options, transparent pricing, and letting you decide what works best.
        </p>
        <p>
          Complete impact window and door replacement for a single-family home in Kendall, FL. The project included <strong>15 windows + 3 French doors</strong> using ES Windows (Premium tier) products. <strong>Total cost: $24,500</strong> including installation, permits, and final inspection. Timeline: <strong>~8 weeks from order to installation</strong>.
        </p>
        <p className="text-sm text-[var(--brand-slate-600)] mt-3 italic">
          This pricing reflects a complete home upgrade with premium-tier products, professional installation, and all required permits.
        </p>
      </>
    ),

    sections: [
      {
        title: "Project Overview",
        testId: "project-overview",
        table: {
          headers: ["Detail", "Specification"],
          rows: [
            ["Location", "Kendall, Miami-Dade County"],
            ["Property Type", "Single-family home (1,850 sq ft)"],
            ["Year Built", "1998"],
            ["Scope", "Full-house replacement"],
            ["Openings", "15 windows + 3 French doors"],
            ["Product Tier", "ES Windows (Premium)"],
            ["Total Cost", "$24,500"],
            ["Timeline", "~8 weeks (order to installation)"]
          ]
        }
      },

      {
        title: "The Situation",
        testId: "the-situation",
        content: (
          <div className="space-y-4">
            <p className="text-[var(--brand-slate-700)]">
              If you're a homeowner in South Florida considering impact windows, this project will feel familiar.
            </p>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">
              What the Homeowner Wanted:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li><strong>Hurricane protection</strong> for the next storm season — the home had original 1990s aluminum single-pane windows with zero impact resistance</li>
              <li><strong>Insurance savings</strong> through wind mitigation — premiums had increased 40% in two years</li>
              <li><strong>Noise reduction</strong> — the home backs up to a busy street with constant traffic noise</li>
              <li><strong>Energy efficiency</strong> — cooling costs were high due to poor insulation and single-pane glass</li>
            </ul>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">
              What the Homeowner Needed:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li><strong>Clear pricing options</strong> — wanted to compare Good, Better, and Premium tiers before making a decision</li>
              <li><strong>No pressure process</strong> — tired of 2-3 hour in-home sales presentations with unclear pricing</li>
              <li><strong>Transparent comparisons</strong> — needed to understand what each tier actually delivers in performance and value</li>
              <li><strong>Control over the decision</strong> — wanted time to review options without being pushed into a same-day commitment</li>
            </ul>
            <p className="mt-4 text-[var(--brand-slate-700)]">
              Does this sound like your situation? This is exactly the type of project we specialize in.
            </p>
          </div>
        )
      },

      {
        title: "Options & Decision Process: Good / Better / Premium",
        testId: "options-decision",
        content: (
          <div>
            <p className="mb-4">
              We provided the homeowner with three tier options. Here's how they compared and why ES Windows (Premium) was chosen:
            </p>
            <p className="text-sm text-[var(--brand-slate-700)] font-semibold mb-4">
              Here's exactly how the options compared for this homeowner:
            </p>
          </div>
        ),
        table: {
          headers: ["Feature", "ECO (Good)", "Mr. Glass (Better)", "ES Windows (Premium)"],
          rows: [
            ["Glass Thickness", "5/16\" laminated", "3/8\" laminated", "7/16\" laminated"],
            ["Frame Quality", "Standard aluminum", "Heavy-duty aluminum", "Premium heavy-duty aluminum"],
            ["Noise Reduction", "Moderate", "Good", "Excellent"],
            ["Energy Efficiency", "Standard", "Improved (Low-E available)", "Maximum (Low-E included)"],
            ["Warranty", "Standard", "Enhanced", "Lifetime"],
            ["Handle Included", "No (add $200-$300)", "No (add $200-$300)", "Yes"],
            ["Est. Cost (15 windows + 3 doors)", "~$18,500", "~$21,000", "~$24,500"]
          ]
        },
        highlight: "The homeowner chose ES Windows because: (1) Noise reduction was a top priority (busy street), (2) Lifetime warranty provided long-term value, (3) Handle included saved $200-$300, (4) Maximum protection for peace of mind during hurricane season. We walk every customer through this same comparison so you can make the right decision without pressure."
      },

      {
        title: "Why This Approach Matters",
        testId: "why-this-matters",
        bullets: [
          <><strong>Removes Pressure:</strong> You review options on your own time without a salesperson in your home. No same-day commitments or high-pressure tactics.</>,
          <><strong>Gives You Control:</strong> You decide which tier fits your priorities and budget. We don't upsell — we explain the differences and let you choose.</>,
          <><strong>Avoids Overpaying:</strong> Some homeowners don't need Premium. If Good or Better fits your goals, we'll tell you. Transparent pricing means you only pay for what you actually need.</>,
          <><strong>Clear Comparisons:</strong> Side-by-side product specs, pricing, and performance data — no hidden details or surprise costs.</>
        ],
        highlight: "This is how window replacement should work: clear options, honest advice, and no games."
      },

      {
        title: "Detailed Scope of Work",
        testId: "scope-work",
        bullets: [
          <><strong>Living Room:</strong> 3 horizontal roller windows + 1 picture window</>,
          <><strong>Master Bedroom:</strong> 2 horizontal roller windows</>,
          <><strong>Bedroom 2 & 3:</strong> 2 horizontal roller windows each</>,
          <><strong>Kitchen:</strong> 2 horizontal roller windows</>,
          <><strong>Bathrooms:</strong> 1 single hung window + 1 horizontal roller window</>,
          <><strong>Entry & Patio:</strong> 1 front French door + 2 French patio doors</>
        ]
      },

      {
        title: "The Solution: Why ES Windows (Premium Tier)",
        testId: "product-selection",
        content: (
          <p className="mb-4">
            The homeowner chose ES Windows (Premium tier) for several strategic reasons:
          </p>
        ),
        bullets: [
          <><strong>Maximum Protection:</strong> 7/16" laminated impact glass provides the highest level of hurricane protection available in South Florida — critical for peace of mind during storm season</>,
          <><strong>Noise Reduction Priority:</strong> The heavy-duty aluminum frames and thick laminated glass significantly reduce street noise — a top concern for this location</>,
          <><strong>Long-Term Value:</strong> ES Windows are built to last 30+ years with minimal maintenance, justifying the premium cost</>,
          <><strong>Entry Door Benefit:</strong> ES Windows includes a handle with the front door (ECO and Mr Glass do not), saving ~$200\u2013$300</>,
          <><strong>Aesthetic Match:</strong> White exterior/white interior perfectly matches the home's existing trim and architectural style</>,
          <><strong>Full HVHZ Compliance:</strong> Miami-Dade NOA certified, ensuring maximum insurance discounts</>
        ],
        highlight: "The homeowner prioritized long-term performance and noise reduction over upfront cost. ES Windows delivered on both."
      },

      {
        title: "Project Breakdown & Pricing Transparency",
        testId: "cost-breakdown",
        content: (
          <p className="mb-4">
            Total project cost: <strong>$24,500</strong> (all-inclusive)
          </p>
        ),
        bullets: [
          "15 horizontal roller windows (various sizes): ~$12,400",
          "1 picture window (large living room): ~$1,070",
          "1 single hung window (bathroom): ~$1,000",
          "3 French doors (front entry + 2 patio): ~$10,000",
          "All includes: product, installation labor, old window removal, caulking, cleanup"
        ],
        highlight: "Final pricing includes all labor, materials, permit fees ($500), old window removal, disposal, and final inspection coordination. No hidden fees or surprise charges."
      },

      {
        title: "Pricing Context: What Affects Cost",
        testId: "pricing-context",
        content: (
          <p className="mb-4">
            Your project cost will depend on several factors:
          </p>
        ),
        bullets: [
          <><strong>Product Tier:</strong> Good ($18k-$20k) vs Better ($21k-$23k) vs Premium ($24k-$26k) for a similar scope</>,
          <><strong>Number of Openings:</strong> More windows and doors = higher total cost</>,
          <><strong>Size & Complexity:</strong> Large picture windows, French doors, and custom sizes cost more than standard windows</>,
          <><strong>Installation Complexity:</strong> Multi-story homes, difficult access, or structural modifications can affect labor costs</>,
          <><strong>Geographic Location:</strong> Permit fees and labor rates vary by city and county in South Florida</>
        ],
        highlight: "Every home is different, which is why we provide multiple options instead of a one-size-fits-all quote. You decide which tier and features make sense for your priorities and budget."
      },

      {
        title: "",
        testId: "mid-page-cta",
        content: (
          <div className="bg-gradient-to-br from-[var(--brand-ocean-50)] to-[var(--brand-ocean-100)] rounded-2xl p-8 text-center border-2 border-[var(--brand-ocean-200)]">
            <p className="text-lg text-[var(--brand-slate-700)] mb-4 font-semibold">
              Compare Good, Better, and Premium options for your home.
            </p>
            <Button
              onClick={() => window.location.href = '/get-quote?src=case_kendall'}
              className="bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white font-semibold rounded-xl h-12 px-8"
            >
              See My Options
            </Button>
            <p className="text-sm text-[var(--brand-slate-600)] mt-4">
              No in-home appointment required. No pressure. Just clear pricing.
            </p>
          </div>
        )
      },

      {
        title: "Timeline Breakdown",
        testId: "timeline",
        table: {
          headers: ["Phase", "Duration", "Details"],
          rows: [
            ["Permit Preparation & Submission", "2 days", "Prepared plans and submitted permit application"],
            ["Permit Approval", "3 business days", "Fast-tracked approval due to complete documentation"],
            ["Manufacturing (ES Windows)", "6–8 weeks", "Premium tier manufacturing lead time"],
            ["Installation", "Few days", "Complete removal and installation by licensed crew"],
            ["Final Inspection", "1 day", "Inspector verified code compliance"],
            ["Project Completion", "—", "Total: ~8 weeks from order to final approval"]
          ]
        },
        highlight: "Inspection Note: Final inspection was completed quickly due to proper preparation, including engineering documentation for concealed bucks and fasteners submitted with the permit. Timeline Note: Since this project did not require HOA approval, we were able to move forward with permitting and ordering simultaneously. In some cases, HOA or board approval is required before ordering materials—especially when selecting frame colors, glass types, or design features—which can extend the overall timeline."
      },

      {
        title: "Challenges & Solutions",
        testId: "challenges",
        content: (
          <p className="mb-3">
            <strong>Challenge:</strong> Managing manufacturing timelines and coordinating a full-home installation with multiple door systems and window types.
          </p>
        ),
        bullets: [
          <><strong>Solution:</strong> Worked closely with the homeowner to coordinate delivery timing and ensure installation crew was scheduled for optimal efficiency.</>,
          <><strong>Result:</strong> Project completed on schedule with no delays despite complex scope involving 3 French door systems and 15 windows of varying types.</>
        ]
      },

      {
        title: "Results & Homeowner Benefits",
        testId: "results",
        bullets: [
          <><strong>Insurance savings:</strong> 28% premium reduction after wind mitigation inspection — saving $840/year</>,
          <><strong>Energy savings:</strong> Measured 15% reduction in summer cooling costs — saving ~$180/year</>,
          <><strong>Noise reduction:</strong> Dramatic improvement — street noise reduced by an estimated 60-70%</>,
          <><strong>Hurricane protection:</strong> Full HVHZ compliance; home is now fully protected for Category 5 hurricanes</>,
          <><strong>Resale value:</strong> Estimated $15,000–$20,000 increase in property value based on comparable sales in Kendall</>,
          <><strong>Total annual savings:</strong> $1,020/year (insurance + energy combined)</>
        ],
        highlight: "Payback Period: ~24 years through savings alone, while enjoying immediate comfort, protection, and increased home value. Project completed July 2025."
      },

      {
        title: "Before & After Video",
        testId: "before-after-video",
        content: (
          <div className="space-y-4">
            <p className="text-[var(--brand-slate-600)] mb-4">
              Watch the complete transformation of this Kendall home from start to finish:
            </p>
            <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-[var(--brand-ocean-100)]">
              <div className="relative bg-black aspect-video">
                <video 
                  controls 
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-contain"
                  data-testid="kendall-case-study-video"
                >
                  <source src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1gpqqfsg_WhatsApp%20Video%202026-03-03%20at%209.22.47%20AM.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
            <p className="text-sm text-[var(--brand-slate-600)] italic text-center">
              Full home impact window replacement: 15 windows + 3 French doors installed over 2 days
            </p>
          </div>
        )
      },

      {
        title: "Homeowner Quote",
        testId: "testimonial",
        content: (
          <div className="bg-[var(--brand-ocean-50)] rounded-xl p-6 border-2 border-[var(--brand-ocean-200)] italic">
            <p className="text-[var(--brand-slate-700)] leading-relaxed mb-3">
              "The remote quoting process was incredibly convenient. I didn't have to sit through a 2-hour sales pitch. I got clear pricing, chose the Better tier, and the whole thing was done in a month. The installers were professional, and my insurance company gave me a 28% discount right away. Absolutely worth it."
            </p>
            <p className="text-sm text-[var(--brand-slate-600)] font-semibold">
              — Sarah M., Kendall, FL (June 2024)
            </p>
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "See Another Project",
        description: "North Miami waterfront condo case study",
        url: "/case-study-north-miami-condo"
      },
      {
        title: "Impact Window Pricing",
        description: "See current pricing for your home",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "Get Your Quote",
        description: "Upload photos and get pricing",
        url: "/get-quote?src=case_kendall"
      }
    ],

    ctaText: "Get My Price",
    ctaSubtext: "Upload photos and receive Good, Better, and Premium pricing options tailored to your home. Compare tiers, review differences, and choose what works best for you. No in-home appointment required. No pressure. Just clear pricing. Takes less than 2 minutes.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Case Studies", link: "/#case-studies" },
      { label: "Kendall Full House Project" }
    ]
  };

  // Article + Breadcrumb schema. Real, visible content only — headline
  // matches H1 / pageData.title, breadcrumbs match what's actually rendered.
  const schemas = [
    buildArticleSchema({
      url: '/case-study-kendall-full-house',
      headline: pageData.title,
      description:
        'Real Kendall full-home impact window and door replacement: 15 windows + 3 French doors using ES Windows. Total cost $24,500 including permits. ~8 week timeline.',
      datePublished: '2026-04-01',
    }),
    buildBreadcrumbSchema(pageData.breadcrumbs),
  ];

  return (
    <>
      <SchemaMarkup schemas={schemas} instanceId="case-kendall" />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
